import moment from 'moment';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserAvatar } from 'components/UserAvatar';
import {
  type AtlasGqlGetCommentsQuery,
  type AtlasGqlUpdateCommentInput,
} from 'types/atlas-graphql';
import { COMMENT_CREATE } from 'utils/access-control/rules';
import { useAccountContext } from 'utils/account/AccountContext';
import { type SingularElement } from 'utils/types';
import { RenderWithAccess } from 'utils/usePermissions';

import AddComment from './AddComment';
import Comment from './Comment';
import { StyledCommentsWrapper, StyledSpinner } from './Comments.style';
import DeleteComment from './DeleteComment';
import EditComment from './EditComment';
import { type RefetchConfig } from './refetchConfig';

const FromNow: FC<{
  timestamp: number;
}> = ({ timestamp }) => {
  const time = moment.unix(timestamp);
  return <span title={time.format('lll')}>{time.fromNow()}</span>;
};

export type CommentThread = AtlasGqlGetCommentsQuery['comments'];
export type SingleComment = SingularElement<CommentThread>;

export interface CommentListProps {
  comments: CommentThread;
  className?: string;
  onSave?: (text: string) => void;
  onEdit?: (input: AtlasGqlUpdateCommentInput) => void;
  editId?: string | null;
  saveLoading?: boolean;
  editLoading?: boolean;
  loading?: boolean;
  columnLayout?: boolean;
  createRule?: string[];
  areWriteActionsVisible?: boolean;
  refetchConfig?: RefetchConfig;
  maxHeight?: string;
}

const CommentList: FC<CommentListProps> = ({
  comments = [],
  className,
  onSave,
  onEdit,
  editId,
  saveLoading,
  editLoading,
  loading,
  columnLayout,
  createRule = [COMMENT_CREATE],
  areWriteActionsVisible = true,
  refetchConfig = {},
  maxHeight,
}) => {
  const { t } = useTranslation();
  const { user } = useAccountContext();
  const commentsRef = useRef<HTMLDivElement | null>(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const [commentsLength, setCommentsLength] = useState(0);

  useEffect(() => {
    const commentsNode = commentsRef.current;
    if (!commentsNode) {
      return;
    }
    const commentCount = comments?.length ?? 0;
    // Check if the comments length has increased
    if (commentCount > commentsLength) {
      commentsNode.scrollTop = commentsNode.scrollHeight;
    }
    setCommentsLength(commentCount);
    const scrollListener = () => {
      if (commentsNode.scrollTop === 0) {
        setIsAtTop(true);
      } else {
        setIsAtTop(false);
      }
    };
    commentsNode.addEventListener('scroll', scrollListener);
    return () => {
      commentsNode.removeEventListener('scroll', scrollListener);
    };
  }, [comments, commentsLength]);

  const getActions = useCallback(
    (comment: SingleComment): JSX.Element[] => {
      const actions: JSX.Element[] = [];
      if (!comment) {
        return actions;
      }
      const isAuthor = comment.author?.id === user.id;
      if (isAuthor) {
        actions.push(
          <DeleteComment
            id={comment.id}
            hideDeleteIcon
            type="link"
            deleteBtnCss={{
              fontSize: '0.75rem !important',
              paddingLeft: '0 !important',
              height: 'unset !important',
            }}
            refetchConfig={refetchConfig}
          />
        );
      }
      if (isAuthor && onEdit) {
        actions.push(<EditComment comment={comment} onEdit={onEdit} loading={editLoading} />);
      }
      return actions;
    },
    [user, onEdit, editLoading, refetchConfig]
  );

  return (
    <StyledCommentsWrapper
      className={className}
      columnLayout={columnLayout}
      $isAtTop={isAtTop}
      maxHeight={maxHeight}
    >
      <div data-testid="comments" ref={commentsRef}>
        {loading ? (
          <StyledSpinner />
        ) : comments?.length ? (
          comments.map(comment => {
            const { id, author, text, timestamp } = comment;
            const name =
              author?.firstName && author?.lastName
                ? `${author.firstName} ${author.lastName}`
                : null;
            // we want to fallback to authorEmail in case author (User) resolves to null
            const email = author?.email ?? comment.authorEmail;
            const title = name || email || '(Invalid User)';
            return (
              <Comment
                className={`testid-comment-${id}`}
                key={id}
                avatar={author && <UserAvatar user={author} />}
                author={<span title={title}>{title}</span>}
                datetime={<FromNow timestamp={Number(timestamp) / 1000} />}
                actions={areWriteActionsVisible ? getActions(comment) : []}
                content={editLoading && id === editId ? <StyledSpinner /> : text}
              />
            );
          })
        ) : (
          <em>{t('comments.noCommentsYet', 'no comments yet...')}</em>
        )}
        {saveLoading && <StyledSpinner />}
      </div>
      <RenderWithAccess rules={createRule}>
        {areWriteActionsVisible && onSave ? (
          <AddComment onSave={onSave} columnLayout={columnLayout} />
        ) : null}
      </RenderWithAccess>
    </StyledCommentsWrapper>
  );
};

export default CommentList;
